import { useEffect, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel,
  Alert, Stack, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { enableInspector, getInspectorConfig } from '../../../services/inspectorApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { POLL_INTERVAL_MS } from './pollInterval';
import MultiSelectField from './MultiSelectField';
import OperationProgress from './OperationProgress';
import { INSPECTOR_SCAN_TYPES, SCAN_TYPE_PRICE_DISCLAIMER } from './inspectorScanTypes';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, actionRowSx, insetPanelSx,
} from '../portalStyles';

// Must match `color` in engines/inspector.js and InspectorEntry.js.
const INSPECTOR_COLOR = '#7D3C98';
const INSPECTOR_COLOR_DARK = '#652F7D'; // one dark per engine, as Detective does — was #68317F here and #5B2C6F on the dashboard

// NO ADMIN-ACCOUNT STEP, and there must never be one. Every other capability
// nominates a delegated administrator; Inspector is enabled standalone in each
// account (Prism has no management-account route), so no enrolled account is
// privileged and EnableInspectorRequest carries no adminAccountId. There is no
// member-email step either — Inspector v2 has no invitation model.
const steps = ['Accounts', 'Regions', 'Scan types', 'Review'];
const ACCOUNTS_STEP = 0;
const REGIONS_STEP = 1;
const SCAN_TYPES_STEP = 2;
const REVIEW_STEP = 3;

// The scan-type list and its copy now live in inspectorScanTypes.js, shared with
// InspectorSettingsPage — see that file for why it is a wire contract and why it
// is not derived from the engine descriptor's shorter `capability.items`.

// Cheapest useful default: the two surfaces almost every account has. This stays
// wizard-local: it is a first-run suggestion, not part of the backend contract,
// and the settings page seeds its checkboxes from the stored config instead.
const DEFAULT_SCAN_TYPES = ['EC2', 'ECR'];

// Inspector-accented primary button (the theme default is the CSPM blue).
const inspectorButtonSx = {
  backgroundColor: INSPECTOR_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: INSPECTOR_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const quietButtonSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  color: CK.body,
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const captionTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

// One row of the Review step — a fixed label column plus the chosen value, so
// every row shares a single baseline grid.
function ReviewRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 160, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function InspectorSetup({ onComplete, priorFailure }) {
  const [active, setActive] = useState(ACCOUNTS_STEP);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state — exactly the four fields of models.EnableInspectorRequest.
  const [homeRegion, setHomeRegion] = useState('us-east-1');
  const [linkingRegions, setLinkingRegions] = useState(['us-east-1']);
  const [memberAccountIds, setMemberAccountIds] = useState([]);
  const [resourceTypes, setResourceTypes] = useState(DEFAULT_SCAN_TYPES);

  // Post-submit state: the config row the POST answered with.
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSecurityHubAccounts();
        if (cancelled) return;
        setAccounts(res?.data?.accounts || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll while the enable fan-out runs. Keyed on the status rather than the whole
  // config so the interval is not rebuilt on every progress tick.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getInspectorConfig();
        if (res?.data) setConfig(res.data);
      } catch (_) {
        // Transient failures are swallowed: the next tick retries, and a dead
        // backend already shows up as a stalled progress bar rather than as a
        // wizard that throws away a run which may well be succeeding.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config?.status]);

  // Hand back to InspectorEntry the moment the run settles, whatever it settled
  // as. This also covers the (unlikely) race where the fan-out finishes before
  // the POST response is rendered — without it the poll above would never start
  // and the wizard would sit on a terminal card with no way forward.
  //
  // Entry then routes on the settled status: enabled/partial_failure to the
  // dashboard, failed back to this wizard with priorFailure set.
  useEffect(() => {
    if (config && config.status !== 'in_progress' && onComplete) onComplete();
  }, [config, onComplete]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  const toggleScanType = (value) => {
    setResourceTypes((prev) => (
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    ));
  };

  const stepComplete = (step) => {
    if (step === ACCOUNTS_STEP) return memberAccountIds.length > 0;
    // The backend force-prepends the home region to linkingRegions anyway; the
    // membership check exists so the Review step cannot understate the blast
    // radius (and the cost) of what is about to be enabled.
    if (step === REGIONS_STEP) {
      return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    }
    // EnableInspector rejects an empty list with a 400. Blocking here is a better
    // experience than failing after the Review step.
    if (step === SCAN_TYPES_STEP) return resourceTypes.length > 0;
    return true;
  };

  const canSubmit = stepComplete(ACCOUNTS_STEP)
    && stepComplete(REGIONS_STEP)
    && stepComplete(SCAN_TYPES_STEP);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await enableInspector({
        homeRegion,
        linkingRegions,
        memberAccountIds,
        resourceTypes,
      });
      // Guard the empty envelope: without it a 2xx carrying no config would leave
      // the wizard sitting on Review with the button re-enabled and nothing said.
      if (!res?.data) throw new Error('Inspector was not enabled — the server returned no configuration.');
      setConfig(res.data);
    } catch (e) {
      // portalFetch rejects with the backend's error string and no status code,
      // so there is nothing to branch on. Every rejection here is a validation
      // failure against one of the three inputs ("account … does not belong to
      // this organization", "unsupported scan type", "already enabled"), and the
      // user can reach all three with Back — so the message stays on Review
      // rather than guessing which step to jump to.
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const accountLabel = (id) => {
    const acct = accounts.find((a) => a.accountId === id);
    return acct ? `${acct.name || acct.accountId} (${acct.accountId})` : id;
  };

  const scanTypeSummary = resourceTypes.length
    ? INSPECTOR_SCAN_TYPES.filter((t) => resourceTypes.includes(t.value)).map((t) => t.label).join(', ')
    : 'None selected';

  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up Amazon Inspector…',
        enabled: 'Amazon Inspector is enabled',
        partial_failure: 'Amazon Inspector enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'Amazon Inspector is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...bodyTextSx, mt: 1 }}>
                Granting scoped permissions and turning on Inspector in each
                account. Accounts are enrolled independently, so results appear as
                each one finishes.
              </Typography>
              <OperationProgress
                percent={config.progress}
                label={config.progressLabel || 'Working…'}
                sx={{ my: 2 }}
              />
            </>
          )}
          {config.status === 'partial_failure' && (
            <Typography sx={{ ...bodyTextSx, mt: 1 }}>
              Some accounts could not be enrolled. The rest are already scanning —
              retry the failed ones, or repair their role permissions, from the
              Enrollment page.
            </Typography>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>{config.errorMessage}</Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <ReviewRow label="Home region" value={config.homeRegion} />
            <ReviewRow label="Accounts" value={String(config.accountCount ?? memberAccountIds.length)} />
          </Box>
        </Box>
      );
    }

    if (active === ACCOUNTS_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the accounts to scan. Inspector runs standalone in each one, so
            there is no administrator account to nominate and no account is
            privileged over another. You can add or remove accounts later without
            tearing anything down.
          </Typography>
          <MultiSelectField
            label="Accounts"
            options={accounts.map((a) => ({
              value: a.accountId,
              label: `${a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
          {accounts.length === 0 && (
            <Typography sx={{ ...captionTextSx, mt: 1 }}>
              No linked AWS accounts were returned for this organization.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      );
    }

    if (active === REGIONS_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Inspector is enabled separately in every region you pick, and every
            region scans and bills on its own. Unlike the other capabilities the
            home region aggregates nothing — Prism does that itself — but it is
            always scanned and is the region Prism talks to by default.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Scanned regions"
            options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
          />
        </Box>
      );
    }

    if (active === SCAN_TYPES_STEP) {
      // Stays wizard-local rather than moving into inspectorScanTypes.js with the
      // list itself. That module mirrors a closed backend allowlist and has to
      // stay diffable against the Go map at a glance; this is not a validity rule
      // — the backend happily accepts LAMBDA_CODE on its own — but a first-run
      // nudge, worded for someone who has not enabled anything yet. Settings
      // deliberately does not repeat it: there the combination is an existing,
      // possibly deliberate choice, and re-raising it on every visit is nagging.
      const codeWithoutPackages = resourceTypes.includes('LAMBDA_CODE')
        && !resourceTypes.includes('LAMBDA');
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Choose what Inspector scans in every enrolled account and region. This
            is the cost lever: AWS bills per resource scanned, not per account, so
            each box you tick applies to every account you selected.
          </Typography>
          <Box sx={insetPanelSx}>
            <FormGroup>
              {INSPECTOR_SCAN_TYPES.map((t) => (
                <FormControlLabel
                  key={t.value}
                  sx={{ alignItems: 'flex-start', mb: 1.5, mr: 0 }}
                  control={
                    <Checkbox
                      checked={resourceTypes.includes(t.value)}
                      onChange={() => toggleScanType(t.value)}
                      sx={{ pt: 0.25, '&.Mui-checked': { color: INSPECTOR_COLOR } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: FONT_BODY,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: CK.navy,
                        }}
                      >
                        {t.label}
                      </Typography>
                      <Typography sx={bodyTextSx}>{t.description}</Typography>
                      <Typography sx={captionTextSx}>{t.price}</Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
            <Typography sx={{ ...captionTextSx, mt: 1 }}>
              {SCAN_TYPE_PRICE_DISCLAIMER}
            </Typography>
          </Box>
          {codeWithoutPackages && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Code scanning alone finds insecure code you wrote, but leaves
              vulnerable third-party dependencies in the deployed package
              unscanned. Most deployments want both Lambda boxes.
            </Alert>
          )}
          {resourceTypes.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Pick at least one scan type — Inspector cannot be enabled with none.
            </Alert>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
        <ReviewRow
          label="Accounts"
          value={memberAccountIds.length <= 3
            ? memberAccountIds.map(accountLabel).join(', ')
            : `${memberAccountIds.length} accounts`}
        />
        <ReviewRow label="Home region" value={homeRegion} />
        <ReviewRow label="Scanned regions" value={linkingRegions.join(', ')} />
        <ReviewRow label="Scan types" value={scanTypeSummary} />
        <Alert severity="info" sx={{ mt: 2 }}>
          Inspector will be turned on in {memberAccountIds.length} account
          {memberAccountIds.length === 1 ? '' : 's'} × {linkingRegions.length} region
          {linkingRegions.length === 1 ? '' : 's'} and starts billing per resource
          scanned as soon as it is enabled. Enrollment runs per account, so a
          failure in one does not stop the others.
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<BugReportOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Set up Inspector"
          subtitle="Enable Amazon Inspector vulnerability scanning across your organization"
          color={INSPECTOR_COLOR}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          {priorFailure && !config && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              The previous setup attempt failed: {priorFailure}. Submitting again will clear that state and retry.
            </Alert>
          )}
          <Stepper
            activeStep={active}
            alternativeLabel
            sx={{
              mb: 3,
              overflowX: 'auto',
              pb: 1,
              '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
              '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
              '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
              '& .MuiStepIcon-root.Mui-active': { color: INSPECTOR_COLOR },
              '& .MuiStepIcon-root.Mui-completed': { color: INSPECTOR_COLOR },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {body()}
          {!config && (
            <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
              <Button disabled={active === ACCOUNTS_STEP} onClick={back} sx={quietButtonSx}>Back</Button>
              {active < REVIEW_STEP ? (
                <Button
                  variant="contained"
                  disabled={!stepComplete(active)}
                  onClick={next}
                  sx={inspectorButtonSx}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={submitting || !canSubmit}
                  onClick={submit}
                  sx={inspectorButtonSx}
                >
                  {submitting ? 'Enabling…' : 'Enable Inspector'}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
